import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useIsMobile } from '../hooks/useIsMobile'

const CONTENT_MULTIPLIER = 1.6
const PARALLAX_FACTOR = 0.07

const SHOOTING_STARS: { top: string; left: string; delay: string; duration: string; mobile: boolean }[] = [
  { top: '10%', left: '82%', delay: '0s', duration: '7.5s', mobile: true },
  { top: '18%', left: '38%', delay: '4.2s', duration: '9s', mobile: true },
  { top: '32%', left: '65%', delay: '2.4s', duration: '8.2s', mobile: false },
  { top: '6%', left: '55%', delay: '6.8s', duration: '10s', mobile: false },
  { top: '48%', left: '20%', delay: '9.5s', duration: '8.6s', mobile: true },
  { top: '62%', left: '88%', delay: '3.3s', duration: '9.4s', mobile: false },
  { top: '78%', left: '32%', delay: '7.6s', duration: '8.8s', mobile: false },
  { top: '92%', left: '70%', delay: '1.2s', duration: '10.5s', mobile: false },
]

type Nebula = {
  top: string
  left: string
  width: number
  color: string
  opacity: number
  delay: number
}

const PALETTE = [
  'rgba(139,60,240,0.6)',
  'rgba(90,20,180,0.55)',
  'rgba(180,140,255,0.45)',
  'rgba(119,36,217,0.5)',
  'rgba(60,10,140,0.55)',
  'rgba(155,90,255,0.4)',
]

function buildNebulae(isMobile: boolean): Nebula[] {
  const positions: [string, string][] = [
    ['-6%', '8%'],
    ['12%', '68%'],
    ['38%', '20%'],
    ['52%', '80%'],
    ['70%', '4%'],
    ['86%', '55%'],
  ]
  return positions.map(([top, left], i) => ({
    top,
    left,
    width: (isMobile ? 58 : 74) + ((i * 37) % 30),
    color: PALETTE[i % PALETTE.length],
    opacity: 0.45 + ((i * 13) % 20) / 100,
    delay: (i * 1.3) % 5,
  }))
}

export function GalaxyBackground() {
  const isMobile = useIsMobile()
  const innerRef = useRef<HTMLDivElement>(null)
  const dustCanvasRef = useRef<HTMLCanvasElement>(null)
  const netCanvasRef = useRef<HTMLCanvasElement>(null)
  const [contentHeight, setContentHeight] = useState(() =>
    typeof window !== 'undefined' ? Math.ceil(window.innerHeight * CONTENT_MULTIPLIER) : 0,
  )
  const [nebulae] = useState(() => buildNebulae(isMobile))
  const maxOffsetRef = useRef(0)

  useEffect(() => {
    function recalc() {
      const h = Math.ceil(window.innerHeight * CONTENT_MULTIPLIER)
      setContentHeight(h)
      maxOffsetRef.current = window.innerHeight * 0.4
    }
    recalc()
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [])

  useEffect(() => {
    let ticking = false
    function onScroll() {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        if (innerRef.current) {
          const offset = -Math.min(window.scrollY * PARALLAX_FACTOR, maxOffsetRef.current)
          innerRef.current.style.transform = `translate3d(0, ${offset}px, 0)`
        }
        ticking = false
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!contentHeight) return
    const canvas = netCanvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.3 : 2)
    const width = window.innerWidth
    canvas.width = width * dpr
    canvas.height = contentHeight * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${contentHeight}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const count = isMobile ? 46 : 85
    const points = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: Math.random() * contentHeight,
      r: Math.random() * 1.5 + 0.7,
    }))

    const threshold = width < 640 ? 120 : 170
    ctx.clearRect(0, 0, width, contentHeight)
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const dx = points[i].x - points[j].x
        const dy = points[i].y - points[j].y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < threshold) {
          ctx.strokeStyle = `rgba(178, 130, 255, ${(1 - dist / threshold) * 0.28})`
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(points[i].x, points[i].y)
          ctx.lineTo(points[j].x, points[j].y)
          ctx.stroke()
        }
      }
    }
    ctx.shadowColor = 'rgba(200, 165, 255, 0.9)'
    for (const p of points) {
      ctx.shadowBlur = p.r * 6
      ctx.beginPath()
      ctx.fillStyle = 'rgba(226, 208, 255, 0.95)'
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.shadowBlur = 0
  }, [contentHeight, isMobile])

  useEffect(() => {
    if (!contentHeight) return
    const canvas = dustCanvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.3 : 2)
    const width = window.innerWidth
    canvas.width = width * dpr
    canvas.height = contentHeight * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${contentHeight}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const count = isMobile ? 180 : 320
    const stars = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: Math.random() * contentHeight,
      r: Math.random() * 1.6 + 0.9,
      a: Math.random() * 0.4 + 0.6,
      speed: Math.random() * 0.5 + 0.15,
      phase: Math.random() * Math.PI * 2,
    }))

    const bigCount = isMobile ? 10 : 20
    const bigStars = Array.from({ length: bigCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * contentHeight,
      r: Math.random() * 1.3 + 1.8,
      speed: Math.random() * 0.4 + 0.1,
      phase: Math.random() * Math.PI * 2,
    }))

    let raf = 0
    function draw(t: number) {
      if (!ctx) return
      ctx.clearRect(0, 0, width, contentHeight)
      ctx.shadowColor = 'rgba(214, 190, 255, 0.9)'
      for (const s of stars) {
        const flicker = 0.55 + 0.45 * Math.sin(t * 0.001 * s.speed + s.phase)
        ctx.shadowBlur = s.r * 5
        ctx.beginPath()
        ctx.fillStyle = `rgba(240, 232, 255, ${s.a * flicker})`
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.shadowColor = 'rgba(255, 255, 255, 0.95)'
      for (const s of bigStars) {
        const flicker = 0.7 + 0.3 * Math.sin(t * 0.0009 * s.speed + s.phase)
        ctx.shadowBlur = s.r * 9
        ctx.beginPath()
        ctx.fillStyle = `rgba(255, 255, 255, ${0.95 * flicker})`
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.shadowBlur = 0
      raf = requestAnimationFrame(draw)
    }
    raf = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(raf)
  }, [contentHeight, isMobile])

  return (
    <div className="fixed inset-0 z-0 overflow-hidden bg-space-950">
      <div
        ref={innerRef}
        className="absolute inset-x-0 top-0 will-change-transform"
        style={{ height: contentHeight || '160%' }}
      >
        {nebulae.map((n, i) => (
          <div
            key={i}
            className="animate-pulse-slow absolute rounded-full blur-[85px]"
            style={{
              top: n.top,
              left: n.left,
              width: `${n.width}vh`,
              height: `${n.width}vh`,
              background: `radial-gradient(circle, ${n.color}, transparent 70%)`,
              opacity: n.opacity,
              animationDelay: `${n.delay}s`,
            }}
          />
        ))}

        <canvas ref={netCanvasRef} className="absolute inset-0" />
        <canvas ref={dustCanvasRef} className="absolute inset-0" />

        {SHOOTING_STARS.filter((s) => !isMobile || s.mobile).map((s, i) => (
          <span
            key={i}
            className="shooting-star"
            style={
              {
                top: s.top,
                left: s.left,
                animationDelay: s.delay,
                '--shoot-duration': s.duration,
              } as CSSProperties
            }
          />
        ))}
      </div>

      <div className="pointer-events-none absolute inset-0 tech-grid" />
      <div className="pointer-events-none absolute inset-0 noise-overlay" />
    </div>
  )
}
